import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant site ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send active site ID for multi-tenant scoping
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    config.headers['x-site-id'] = activeSiteId;
  }
  return config;
});

// Add response interceptor to handle module access errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response.data?.module) {
      // This is a module access error
      const moduleError = error.response.data;
      
      // Store the error for UI components to handle
      localStorage.setItem('moduleAccessError', JSON.stringify(moduleError));
      
      // Redirect to the module access error page
      window.location.href = '/module-access-error';
      
      // Prevent the error from propagating
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Types
export interface ProfitLossStatement {
  period: {
    startDate: string;
    endDate: string;
    periodId?: string;
  };
  revenue: {
    total: number;
    items: Array<{
      accountId: string;
      code: string;
      name: string;
      amount: number;
    }>;
  };
  costOfGoodsSold: {
    total: number;
    items: Array<{
      accountId: string;
      code: string;
      name: string;
      amount: number;
    }>;
  };
  grossProfit: number;
  operatingExpenses: {
    total: number;
    categories: {
      [key: string]: {
        total: number;
        items: Array<{
          accountId: string;
          code: string;
          name: string;
          amount: number;
        }>;
      };
    };
  };
  otherExpenses: {
    total: number;
    items: Array<{
      accountId: string;
      code: string;
      name: string;
      amount: number;
    }>;
  };
  netIncome: number;
  comparison?: {
    revenue: number;
    expenses: number;
    netIncome: number;
  };
  grossProfitMargin: number;
  netProfitMargin: number;
}

export interface BalanceSheet {
  asOfDate: string;
  assets: {
    current: {
      total: number;
      items: Array<{
        accountId: string;
        code: string;
        name: string;
        amount: number;
      }>;
    };
    nonCurrent: {
      total: number;
      items: Array<{
        accountId: string;
        code: string;
        name: string;
        amount: number;
      }>;
    };
    total: number;
  };
  liabilities: {
    current: {
      total: number;
      items: Array<{
        accountId: string;
        code: string;
        name: string;
        amount: number;
      }>;
    };
    nonCurrent: {
      total: number;
      items: Array<{
        accountId: string;
        code: string;
        name: string;
        amount: number;
      }>;
    };
    total: number;
  };
  equity: {
    total: number;
    items: Array<{
      accountId: string;
      code: string;
      name: string;
      amount: number;
    }>;
  };
  totalAssets: number;
  totalLiabilitiesAndEquity: number;
  isBalanced: boolean;
}

export interface CashFlowStatement {
  period: {
    startDate: string;
    endDate: string;
  };
  operatingActivities: {
    inflows: number;
    outflows: number;
    net: number;
    items: Array<{
      date: string;
      description: string;
      amount: number;
      type: 'inflow' | 'outflow';
    }>;
  };
  investingActivities: {
    inflows: number;
    outflows: number;
    net: number;
    items: Array<{
      date: string;
      description: string;
      amount: number;
      type: 'inflow' | 'outflow';
    }>;
  };
  financingActivities: {
    inflows: number;
    outflows: number;
    net: number;
    items: Array<{
      date: string;
      description: string;
      amount: number;
      type: 'inflow' | 'outflow';
    }>;
  };
  netCashFlow: number;
  cashBalance: {
    beginning: number;
    ending: number;
  };
}

export interface TrialBalance {
  asOfDate: string;
  accounts: Array<{
    accountId: string;
    code: string;
    name: string;
    type: string;
    debit: number;
    credit: number;
  }>;
  totalDebits: number;
  totalCredits: number;
  isBalanced: boolean;
}

export interface GeneralLedgerEntry {
  id: string;
  date: string;
  reference?: string;
  referenceType: string;
  description: string;
  lines: Array<{
    accountId: string;
    accountCode: string;
    accountName: string;
    debit: number;
    credit: number;
  }>;
  postedBy?: string;
  postedAt?: string;
}

export interface GeneralLedger {
  period: {
    startDate: string;
    endDate: string;
  };
  accountId?: string;
  openingBalance: number;
  entries: GeneralLedgerEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ReportParams {
  startDate?: string;
  endDate?: string;
  periodId?: string;
  asOfDate?: string;
  accountId?: string;
  compareWith?: string;
  page?: number;
  limit?: number;
}

// API Functions
export const financialReportsAPI = {
  // Profit & Loss Statement
  getProfitAndLoss: (siteId: string, params?: ReportParams) => {
    return api.get<ProfitLossStatement>(`/financial-reports/sites/${siteId}/profit-loss`, { params });
  },

  // Balance Sheet
  getBalanceSheet: (siteId: string, params?: ReportParams) => {
    return api.get<BalanceSheet>(`/financial-reports/sites/${siteId}/balance-sheet`, { params });
  },

  // Cash Flow Statement
  getCashFlow: (siteId: string, params?: ReportParams) => {
    return api.get<CashFlowStatement>(`/financial-reports/sites/${siteId}/cash-flow`, { params });
  },

  // Trial Balance
  getTrialBalance: (siteId: string, params?: ReportParams) => {
    return api.get<TrialBalance>(`/financial-reports/sites/${siteId}/trial-balance`, { params });
  },

  // General Ledger
  getGeneralLedger: (siteId: string, params?: ReportParams) => {
    return api.get<GeneralLedger>(`/financial-reports/sites/${siteId}/general-ledger`, { params });
  }
};

export default financialReportsAPI;
